/**
 * "About" descriptions for the MVX Plus markers — one plain-language,
 * non-diagnostic paragraph per marker, mirroring the app's About-tab content
 * (cat 700000+ block). MVX is NOT a Section and doesn't flow through the
 * Section-keyed About tab (findAboutConsideration), so these live in a STATIC
 * module (like holobiomeKits.ts / mvxSupplements.ts) and render via
 * MvxAboutSection. The same text is mirrored in the About-tab spreadsheet
 * (Section 15, categoryId 701500+) so it can move to the DB later.
 *
 * `id` matches the index/factor id (MvxInputs keys) so the About tab can show
 * only the markers the client has a value for.
 */
export interface MvxAboutEntry {
  id: string;
  title: string;
  description: string;
}

/**
 * How to collect the sample, printed above the per-marker descriptions.
 *
 * Dr. Schmidt, 2026-10-08: no exercise for 24 to 48 hours before the draw,
 * because muscle breakdown can add up to 10 points to the MVX. That is a
 * tenth of the scale and larger than most of the changes a practitioner is
 * trying to read, so it belongs where they will see it before they order
 * rather than buried in a bullet that only fires once the damage is done.
 *
 * It sits in About rather than Insights because it is advice about the TEST,
 * true of every draw, not a reading of this one.
 */
/**
 * What the test is FOR, printed at the very top of About, above the collection
 * note and the per-marker descriptions. Reading order on the tab is therefore:
 * what it measures, how to collect it, what each number means.
 *
 * Dr. Schmidt, 2026-10-19: MVX Plus measures cellular health, not disease. He
 * raised it as the thing that sells the test to a practitioner, and it is the
 * frame the rest of the tab reads under, so it goes first.
 *
 * Here rather than on the Training tab because it is a fact about the TEST,
 * true of every draw, not a pitch for his program; same reasoning that put
 * MVX_COLLECTION_NOTE in About rather than Insights. It is also not a reading
 * of this client's result, which is what keeps it out of the Insights panel.
 *
 * NO ATTRIBUTION LINE, unlike the collection note. That one carries his name
 * because the 24-to-48-hour advice is his own clinical guidance. This is what
 * Labcorp says the test is, so it is the test's own framing and attributing it
 * to him would credit him with a description that is not his (Maryann,
 * 2026-10-21). He happens to have raised it on the call; that is where we heard
 * it, not whose claim it is.
 */
export const MVX_PURPOSE_NOTE = {
  // "What this test is for" rather than "What this test measures", so the
  // heading does not echo the verb two words later.
  title: 'What this test is for',
  // "measures" over an earlier "reads", which was vaguer, and over
  // "identifies", which was dropped because you identify a condition rather
  // than a state and it leans diagnostic, the opposite of the point here.
  description:
    'MVX Plus measures cellular health, not disease. The six NMR markers behind the score describe how well-nourished and well-fueled the cells are, and how much inflammatory burden they are carrying, rather than naming a condition.',
};

export const MVX_COLLECTION_NOTE = {
  title: 'Before the draw',
  description:
    'No exercise for 24 to 48 hours beforehand. Muscle breakdown pulls leucine out of the blood and lets isoleucine rise, which can add as much as 10 points to the MVX score. Dr. Darren Schmidt.',
};

export const MVX_ABOUT: MvxAboutEntry[] = [
  {
    id: 'mvx',
    title: 'MVX (Metabolic Vulnerability Index)',
    description:
      'A composite score from 1 to 100 that summarizes near-term metabolic resilience, read by NMR from six blood markers and combined into two sub-scores — inflammation (IVX) and metabolic strain (MMX). A lower score reflects greater resilience; a higher score reflects more vulnerability.',
  },
  {
    id: 'ivx',
    title: 'IVX (Inflammation Vulnerability Index)',
    description:
      'One of the two MVX sub-scores. It reflects how much inflammation or hidden infection the body is carrying, drawn from GlycA and small HDL particle number. A lower value reflects less inflammatory burden.',
  },
  {
    id: 'mmx',
    title: 'MMX (Metabolic Malnutrition Index)',
    description:
      'One of the two MVX sub-scores. It reflects whether the cells are well-nourished and well-fueled for energy, drawn from citrate and the branched-chain amino acids. A lower value reflects better cellular nourishment and energy supply.',
  },
  {
    id: 'glycA',
    title: 'GlycA',
    description:
      'An NMR-based marker of systemic inflammation. It reflects the overall inflammatory signal in the blood and tends to rise with active inflammation or hidden infection. Lower values reflect a calmer inflammatory state.',
  },
  {
    id: 'sHdlP',
    title: 'Small HDL-P (Small HDL Particle Number)',
    description:
      'Measures the number of small HDL particles in the blood. An adequate level is considered supportive here; low levels can accompany toxic burden, impaired detoxification, or active inflammation drawing these particles down.',
  },
  {
    id: 'citrate',
    title: 'Citrate',
    description:
      "A marker tied to mitochondrial function and cellular energy production. It reflects how steadily the cells' energy pathways are turning over, and is read alongside the amino acids as part of the metabolic sub-score.",
  },
  {
    id: 'valine',
    title: 'Valine',
    description:
      'One of the three branched-chain amino acids (with leucine and isoleucine). It reflects protein and nutrient status and how the body builds and uses amino acids. Values within the optimal band reflect balanced handling — both very low and very high can reflect metabolic strain.',
  },
  {
    id: 'leucine',
    title: 'Leucine',
    description:
      'One of the three branched-chain amino acids (with valine and isoleucine). It supports muscle protein and energy metabolism and reflects protein and nutrient status. Values within the optimal band reflect balanced amino-acid handling.',
  },
  {
    id: 'isoleucine',
    title: 'Isoleucine',
    description:
      'One of the three branched-chain amino acids (with valine and leucine). It reflects protein and nutrient status and amino-acid metabolism. Values within the optimal band reflect balanced amino-acid handling.',
  },
];
